import styled, { css, keyframes } from 'styled-components';

// ============ Animations ============

export const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(16px);
  }
  to { 
    opacity: 1;
    transform: translateY(0);
  }
`;

// ============ Buttons ============

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  font-size: 0.9375rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-family: inherit;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  text-decoration: none;
  white-space: nowrap;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    pointer-events: none;
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`;

export const Button = styled.button`
  ${buttonBase}
  
  ${({ variant, theme }) => {
    switch (variant) {
      case 'primary':
        return css`
          background: ${theme.colors.primary};
          color: white;
          box-shadow: ${theme.shadows.button};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.primaryHover};
            box-shadow: ${theme.shadows.buttonHover};
          }
          
          &:active:not(:disabled) {
            background: ${theme.colors.primaryDark};
          }
        `;
      case 'success':
        return css`
          background: ${theme.colors.success};
          color: white;
          
          &:hover:not(:disabled) {
            background: ${theme.colors.successDark};
          }
        `;
      case 'secondary':
        return css`
          background: #F8FAFC;
          color: ${theme.colors.primary};
          border: 1px solid ${theme.colors.border};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.accentLight};
            border-color: ${theme.colors.primary};
          }
        `;
      case 'text':
        return css`
          background: transparent;
          color: ${theme.colors.primary};
          padding: 12px 16px;
          
          &:hover:not(:disabled) {
            background: ${theme.colors.accentLight};
          }
        `;
      case 'danger':
        return css`
          background: ${theme.colors.danger};
          color: white;
          
          &:hover:not(:disabled) {
            background: ${theme.colors.dangerDark};
          }
        `;
      default:
        return css`
          background: ${theme.colors.primary};
          color: white;
          box-shadow: ${theme.shadows.button};
          
          &:hover:not(:disabled) {
            background: ${theme.colors.primaryHover};
            box-shadow: ${theme.shadows.buttonHover};
          }
        `;
    }
  }}
  
  ${({ $block }) => $block && css`
    width: 100%;
  `}
  
  ${({ $large }) => $large && css`
    padding: 14px 32px;
    font-size: 1rem;
  `}
  
  ${({ $small }) => $small && css`
    padding: 8px 16px;
    font-size: 0.875rem;
  `}
`;

// ============ Cards ============

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.backgroundCard};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: ${({ theme }) => theme.spacing.xl};
  animation: ${slideUp} 0.3s ease;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.lg};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }
`;

// ============ Form Elements ============

export const FormGroup = styled.div`
  margin-bottom: 20px;
`;

export const FormLabel = styled.label`
  display: block;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.875rem;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  font-size: 1rem;
  font-family: inherit;
  background: ${({ theme }) => theme.colors.backgroundCard};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  direction: ltr;
  text-align: right;
  color: ${({ theme }) => theme.colors.text};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textPlaceholder};
  }
  
  &:disabled {
    background: ${({ theme }) => theme.colors.backgroundAlt};
    cursor: not-allowed;
  }
`;

// ============ Alerts ============

export const Alert = styled.div`
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 0.9375rem;
  line-height: 1.5;
  animation: ${fadeIn} 0.2s ease;
  
  ${({ variant, theme }) => {
    switch (variant) {
      case 'error':
        return css`
          background: ${theme.colors.dangerLight};
          color: ${theme.colors.dangerDark};
          border: 1px solid #F5C6C6;
        `;
      case 'success':
        return css`
          background: ${theme.colors.successLight};
          color: ${theme.colors.successDark};
          border: 1px solid #A8DAB5;
        `;
      case 'warning':
        return css`
          background: ${theme.colors.warningLight};
          color: #B06000;
          border: 1px solid #FEEFC3;
        `;
      case 'info':
      default:
        return css`
          background: ${theme.colors.accentLight};
          color: ${theme.colors.primaryDark};
          border: 1px solid #D2E3FC;
        `;
    }
  }}
`;

// ============ Spinner ============

export const Spinner = styled.span`
  display: inline-block;
  width: ${({ size }) => size || '20px'};
  height: ${({ size }) => size || '20px'};
  border: 2px solid ${({ color }) => color || 'currentColor'};
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// ============ Container ============

export const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`;

// ============ Checkbox ============

export const CheckboxWrapper = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: 8px;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: rgba(21, 97, 237, 0.05);
  }

  input[type="checkbox"] {
    width: 20px;
    height: 20px;
    margin-top: 2px;
    cursor: pointer;
    accent-color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }

  label, span {
    flex: 1;
    font-size: 0.9375rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.colors.text};
    font-weight: ${({ theme }) => theme.fontWeights.normal};
  }
`;

// ============ Divider ============

export const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

// ============ Badge ============

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  
  ${({ variant, theme }) => {
    switch (variant) {
      case 'success':
        return css`
          background: ${theme.colors.successLight};
          color: ${theme.colors.successDark};
        `;
      case 'warning':
        return css`
          background: ${theme.colors.warningLight};
          color: #B06000;
        `;
      case 'error':
        return css`
          background: ${theme.colors.dangerLight};
          color: ${theme.colors.dangerDark};
        `;
      default:
        return css`
          background: ${theme.colors.accentLight};
          color: ${theme.colors.primary};
        `;
    }
  }}
`;

// ============ Icon Circle ============

export const IconCircle = styled.div`
  width: ${({ size }) => size || '48px'};
  height: ${({ size }) => size || '48px'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ fontSize }) => fontSize || '24px'};
  
  ${({ variant, theme }) => {
    switch (variant) {
      case 'success':
        return css`
          background: ${theme.colors.successLight};
          color: ${theme.colors.success};
        `;
      case 'error':
        return css`
          background: ${theme.colors.dangerLight};
          color: ${theme.colors.danger};
        `;
      case 'warning':
        return css`
          background: ${theme.colors.warningLight};
          color: ${theme.colors.warning};
        `;
      default:
        return css`
          background: ${theme.colors.accentLight};
          color: ${theme.colors.primary};
        `;
    }
  }}
`;

// ============ Text ============

export const Text = styled.p`
  margin: 0;
  color: ${({ theme, $muted }) => $muted ? theme.colors.textMuted : theme.colors.textSecondary};
  font-size: ${({ $size }) => $size || '1rem'};
  line-height: 1.5;
`;

export const Title = styled.h2`
  margin: 0 0 8px 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ $size }) => $size || '1.5rem'};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;
